import { isEqual, last, max, min } from 'lodash';
import React, { useState } from 'react';
import uPlot, { Series } from 'uplot';
import UplotReact from 'uplot-react';

import 'uplot/dist/uPlot.min.css';

import { SplunkThemeProvider, useSplunkTheme } from '@splunk/themes';
import { abbreviateNumber } from '@splunk/ui-utils/format';

import { TimeScale, def } from './utilities';
import { StyledHeatMap } from './Heatmap.styled';
import { getVizThemeForKey, VizThemeKey, VizTheme } from './theme';
import {
    buildColorFunction,
    timeTickFormatter,
    filterSplit,
    roundToPoT,
    pixelStrToNum,
} from './utilities/utils';

const DEFAULT_LEGEND_HEIGHT = 300;
const DEFAULT_LEGEND_WIDTH = 25;
const LEGEND_WIDTH = 100;
const CHART_GAP = 20;

export interface HeatmapLegendProps {
    yMaxValue: number;
    width: number;
    height: number;
    yLegendLabel: string;
    data: [startEpochSeconds: number[], yStart: number[], yEnd: number[], weight: number[]];
    theme: VizTheme;
}

export type UserAnalytics = () => {
    event: (eventCategory: string, eventAction: string, eventValue?: any, eventLabel?: string) => void;
    pageview?: (page, title) => void;
    setUserInfo?: (userInfo) => void;
};

export interface OverlayParameters {
    uOverRect: { left: number; top: number; height: number; width: number };
    posToVal: (leftTop: number, scaleKey: 'x' | 'y') => number;
    valToPos: (val: number, scaleKey: 'x' | 'y') => number;
}

interface LegendDimensions {
    xOff: number;
    yOff: number;
    xDim: number;
    yDim: number;
    legendWidth: number;
}

interface LegendStyle {
    theme: VizTheme;
    resolutionScale: number;
}

const setupLegendContext = (ctx: CanvasRenderingContext2D, style: LegendStyle): void => {
    const textSize = style.theme.heatmap.axisFontSize * style.resolutionScale;
    // eslint-disable-next-line no-param-reassign
    ctx.font = `${textSize}px Arial`;
};

const renderLegendLabels = (
    ctx: CanvasRenderingContext2D,
    dimensions: LegendDimensions,
    style: LegendStyle,
    yMaxValue: number
): void => {
    const legendValuePosition = (dimensions.legendWidth + 5) * style.resolutionScale;

    // eslint-disable-next-line no-param-reassign
    ctx.fillStyle = style.theme.heatmap.axisStroke;
    ctx.fillText('0', legendValuePosition, dimensions.yOff + dimensions.yDim);
    ctx.fillText(
        abbreviateNumber(yMaxValue >= 10 ? Math.round(yMaxValue) : yMaxValue),
        legendValuePosition,
        dimensions.yOff
    );
};

const renderLegendGradient = (
    ctx: CanvasRenderingContext2D,
    dimensions: LegendDimensions,
    style: LegendStyle,
    yMaxValue: number
): void => {
    const { theme, resolutionScale } = style;
    const { yOff, yDim, legendWidth } = dimensions;

    const scaledLegendWidth = legendWidth * resolutionScale;
    const colors = theme.heatmap.colorScheme;
    const numColors = colors.length;
    const bucketPixelHeight = Math.round(yDim / numColors);
    const legendValuePosition = (legendWidth + 5) * resolutionScale;

    let bucketColorIdx = 0;

    for (let i = 0; i < yDim; i += 1) {
        // eslint-disable-next-line no-param-reassign
        ctx.fillStyle = colors[bucketColorIdx];
        ctx.fillRect(0, yOff + yDim - i, scaledLegendWidth, 1);

        if (i !== 0 && i % bucketPixelHeight === 0) {
            bucketColorIdx += 1;
            const bucketValue = (yMaxValue / numColors) * bucketColorIdx;
            // eslint-disable-next-line no-param-reassign
            ctx.fillStyle = theme.heatmap.axisStroke;
            ctx.fillText(
                abbreviateNumber(bucketValue >= 10 ? Math.round(bucketValue) : bucketValue),
                legendValuePosition,
                yOff + yDim - i
            );
        }
    }
};

const renderLegendContent = (
    ctx: CanvasRenderingContext2D,
    dimensions: LegendDimensions,
    theme: VizTheme,
    yMaxValue: number
): void => {
    const resolutionScale = window.devicePixelRatio || 1;
    const style: LegendStyle = { theme, resolutionScale };

    setupLegendContext(ctx, style);
    renderLegendLabels(ctx, dimensions, style, yMaxValue);
    renderLegendGradient(ctx, dimensions, style, yMaxValue);
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const HeatmapLegend = ({ yMaxValue, width, height, yLegendLabel, data, theme }: HeatmapLegendProps) => {
    const buildPathsForLegend =
        (legendWidth: number): uPlot.Series.PathBuilder =>
        (u: uPlot, seriesIdx: number): null => {
            uPlot.orient(
                u,
                seriesIdx,
                // eslint-disable-next-line max-params -- uPlot.orient callback signature
                (series, dataX, dataY, scaleX, scaleY, valToPosX, valToPosY, xOff, yOff, xDim, yDim) => {
                    const dimensions: LegendDimensions = {
                        xOff,
                        yOff,
                        xDim,
                        yDim,
                        legendWidth,
                    };
                    renderLegendContent(u.ctx, dimensions, theme, yMaxValue);
                }
            );
            return null;
        };

    // Normalize target dimensions to avoid non-zero values.
    const normalizedHeight = height <= 0 ? DEFAULT_LEGEND_HEIGHT : height;
    const normalizedWidth = width <= 0 ? DEFAULT_LEGEND_WIDTH : width;

    const options: uPlot.Options = {
        width: LEGEND_WIDTH,
        height: normalizedHeight,
        mode: 2,
        cursor: { show: false },
        legend: { show: false },
        axes: [
            {
                // invisible axis here to match canvas positioning of heatmap path
                size: 75,
                grid: { show: false },
                ticks: { show: false },
            },
            {
                show: true,
                size: 45,
                label: yLegendLabel,
                labelFont: 'bold 12px Arial',
                side: 1,
                grid: { show: false },
                ticks: { show: false },
                values: [null],
                stroke: theme.heatmap.axisStroke,
            },
        ],
        scales: {
            x: { min: 0, max: normalizedWidth, time: false },
            y: {
                min: 0,
                max: yMaxValue,
            },
        },
        series: [{}, { paths: buildPathsForLegend(DEFAULT_LEGEND_WIDTH) }],
    };

    return <UplotReact options={options} data={[Array(data.length), data] as unknown as uPlot.AlignedData} />;
};

function paths(
    timeInterval: number,
    sendLegendRangeMax: React.Dispatch<React.SetStateAction<number | undefined>>,
    theme: VizTheme,
    sendAnalytics?: (
        eventCategory: string,
        eventAction: string,
        eventValue?: any,
        eventLabel?: string | undefined
    ) => void
): Series.PathBuilder {
    return (u: uPlot, seriesIdx: number): null => {
        uPlot.orient(
            u,
            seriesIdx,
            // eslint-disable-next-line max-params -- uPlot.orient callback signature
            (series, dataX, dataY, scaleX, scaleY, valToPosX, valToPosY, xOff, yOff, xDim, yDim) => {
                try {
                    const [startTimes, yStarts, yEnds, counts] = u.data[seriesIdx] as unknown as [
                        number[],
                        number[],
                        number[],
                        number[],
                    ];
                    const dlen = startTimes.length;
                    // create map where key is startTime and value is array of vertical pixels (length of array is height of heatmap)
                    const startTimePixelMap: Record<string, number[]> = {}; // startTime: big empty array of vertical pixel countsPerSecond

                    let globalMaxCountPerSecond = 0;
                    // for each entry in heatmap data, create an number[] where each value is the countPerSecond of the corresponding heatmap pixel
                    for (let i = 0; i < dlen; i += 1) {
                        const startTime = startTimes[i];
                        const yStart = yStarts[i];
                        const yEnd = yEnds[i];
                        const count = counts[i];
                        const yDimInteger = Math.round(yDim);

                        if (!startTimePixelMap[startTime]) {
                            startTimePixelMap[startTime] = new Array(yDimInteger).fill(0);
                        }

                        const yTopPixelPos = Math.floor(valToPosY(yEnd, scaleY, yDim, 0));
                        const yBottomPixelPos = Math.ceil(valToPosY(yStart, scaleY, yDim, 0));
                        const heightPixels = Math.max(1, yBottomPixelPos - yTopPixelPos); // use min 1 in the unlikely event yStart === yEnd

                        const countPerSecond = count / heightPixels / timeInterval;

                        // for each vertical pixel in the startTime, increment the startTimePixelMap's countPerSecond
                        for (let j = yTopPixelPos; j <= yTopPixelPos + heightPixels; j += 1) {
                            startTimePixelMap[startTime][j] += countPerSecond;
                            const val = startTimePixelMap[startTime][j];
                            if (val) {
                                globalMaxCountPerSecond = Math.max(globalMaxCountPerSecond, val);
                            }
                        }
                    }

                    const colorFn = buildColorFunction(globalMaxCountPerSecond, theme);
                    // eslint-disable-next-line no-param-reassign,prefer-destructuring
                    u.ctx.fillStyle = theme.heatmap.colorScheme[0]; // essentially sets "background color" of heatmap
                    u.ctx.fillRect(xOff, yOff, xDim, yDim);

                    // paint vertical slices for each unique startTime
                    const uniqueStartTimes = Object.keys(startTimePixelMap);
                    uniqueStartTimes.forEach(rectangleStartTime => {
                        const rectanglePixelCountPerSecond = startTimePixelMap[rectangleStartTime];
                        const numberStartTime = Number(rectangleStartTime);
                        for (let j = 0; j < rectanglePixelCountPerSecond.length; j += 1) {
                            const x = valToPosX(numberStartTime, scaleX, xDim, xOff);
                            const width =
                                valToPosX(numberStartTime + timeInterval, scaleX, xDim, xOff) -
                                valToPosX(numberStartTime, scaleX, xDim, xOff);
                            const canvasY = j + yOff;
                            // eslint-disable-next-line no-param-reassign
                            u.ctx.fillStyle = colorFn(rectanglePixelCountPerSecond[j]);
                            u.ctx.fillRect(x, canvasY, width, 1);
                        }
                    });

                    sendAnalytics?.('Data_View/Heatmap_Analyzer', 'View_Load', null, 'RenderSuccess');
                    // send legend range max back to InternalHeatMap
                    sendLegendRangeMax(globalMaxCountPerSecond);
                } catch (e) {
                    sendAnalytics?.('Data_View/Heatmap_Analyzer', 'View_Load', yDim, 'RenderError');
                }
            }
        );
        return null;
    };
}

// Every rectangle (data point) in this heatmap covers a range of time on the x-axis and a range of
// some other measurement on the y-axis. The color of a rectangle maps proportionally to some
// measurement that aggregates within those two ranges.
//
// The time slices must be fixed-interval, but the y-slices can be unevenly sized, and *each time
// slice can have an independent set of y slice points*.
//
// y-axis is linear-only at the moment.
export interface InternalHeatmapProps {
    width: number;
    height: number;
    timeZone: string;
    userAnalytics?: ReturnType<UserAnalytics>;
    // data must have at least two points.
    // data should be sorted by startEpochSeconds, then by yStart.
    data: [startEpochSeconds: number[], yStart: number[], yEnd: number[], weight: number[]];
    // Used to set a wider time range than is present in the data (useful during incremental loading)
    timeRangeEpochSeconds?: {
        start: number;
        end: number;
    };
    yLabel: string;
    yLegendLabel: string;
    // eslint-disable-next-line no-undef
    overlay?: (params: OverlayParameters) => JSX.Element;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const InternalHeatmap = ({
    width,
    height,
    timeZone,
    userAnalytics,
    data,
    timeRangeEpochSeconds = { start: Infinity, end: 0 },
    yLegendLabel,
    yLabel,
    overlay,
}: InternalHeatmapProps) => {
    const { colorScheme } = useSplunkTheme();
    const theme = getVizThemeForKey(colorScheme);

    const [legendRangeMax, setLegendRangeMax] = useState<number>();

    const [overlayParams, setOverlayParams] = useState<OverlayParameters | null>(null);

    let xMin;
    let xMax;
    let yMinRounded;
    let yMaxRounded;
    let xIntervalSeconds;

    const xs = data[0];
    const secondXValue = xs.find(x => x !== xs[0]);
    if (secondXValue) {
        xMin = timeRangeEpochSeconds?.start || xs[0];
        xIntervalSeconds = secondXValue - xs[0];
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        xMax = timeRangeEpochSeconds?.end || last(xs)! + xIntervalSeconds;
        const allYValues = [...data[1], ...data[2]];

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const yMin = min(allYValues)!;
        yMinRounded = roundToPoT(yMin, Math.floor);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const yMax = max(allYValues)!;
        const yMaxNearestPoT = roundToPoT(yMax, Math.floor);

        // Round yMax to the nearest multiple of a power of 10
        yMaxRounded = Math.ceil(yMax / yMaxNearestPoT) * yMaxNearestPoT;
    } else if (timeRangeEpochSeconds) {
        xMin = timeRangeEpochSeconds.start;
        xMax = timeRangeEpochSeconds.end;
        yMinRounded = 1e6;
        yMaxRounded = 1e8;
        xIntervalSeconds = 1;
        // Empty out data to avoid running render code against unexpected data shapes
        // eslint-disable-next-line no-param-reassign
        data = [[], [], [], []];
    } else {
        throw new Error('Heatmap requires two or more points on the x axis, or a timeRangeEpochSeconds prop');
    }

    const heatmapWidth = width - (LEGEND_WIDTH + CHART_GAP);

    const options: uPlot.Options = {
        hooks: {
            setSize: [
                (u: uPlot): void => {
                    if (def(u.over)) {
                        const uOverStyles = u.over.style;
                        const tempRenderProp = {
                            uOverRect: {
                                left: pixelStrToNum(uOverStyles.left),
                                top: pixelStrToNum(uOverStyles.top),
                                height: pixelStrToNum(uOverStyles.height),
                                width: pixelStrToNum(uOverStyles.width),
                            },
                            posToVal: u.posToVal,
                            valToPos: u.valToPos,
                        };
                        if (!isEqual(tempRenderProp.uOverRect, overlayParams?.uOverRect)) {
                            setOverlayParams(tempRenderProp);
                        }
                    }
                },
            ],
        },
        width: heatmapWidth,
        height,
        mode: 2,
        tzDate: ts => uPlot.tzDate(new Date(ts * 1e3), timeZone),
        scales: {
            x: {
                time: true,
                min: xMin,
                max: xMax,
            },
            y: {
                min: yMinRounded,
                max: yMaxRounded,
                distr: 3,
                auto: false,
            },
        },
        series: [
            {},
            {
                paths: paths(xIntervalSeconds, setLegendRangeMax, theme, userAnalytics?.event),
                stroke: theme.heatmap.axisStroke,
            },
        ],
        cursor: {
            y: false,
            bind: {
                dblclick: () => null,
            },
            drag: {
                setScale: false,
            },
        },
        select: {
            show: false,
        } as uPlot.Select,
        axes: [
            {
                size: 75,
                grid: { show: false },
                ticks: { show: true, stroke: theme.heatmap.axisStroke, width: 1 },
                stroke: theme.heatmap.axisStroke,
            },
            {
                show: true,
                size: 75,
                label: yLabel,
                labelGap: 5,
                labelSize: 30,
                filter: (u: uPlot, splits: number[]): (number | null)[] => {
                    const splitsPos = splits.map(split => u.valToPos(split, 'y'));
                    return splits.map((split, index) => filterSplit(index, split, splitsPos, u));
                },
                values: (u: uPlot, splits: (number | null)[]): string[] =>
                    splits.map(split => (split ? timeTickFormatter(split, TimeScale.Nanoseconds) : '')),
                side: 3,
                grid: { show: false },
                ticks: { show: true, stroke: theme.heatmap.axisStroke, width: 1 },
                stroke: theme.heatmap.axisStroke,
            },
        ],
        legend: {
            show: false,
        },
    };
    return (
        <>
            {overlayParams ? overlay?.(overlayParams) : null}
            <StyledHeatMap data-testid="heatmap-root" gap={CHART_GAP} theme={theme}>
                <UplotReact
                    options={options}
                    data={[Array(data.length), data] as unknown as uPlot.AlignedData}
                />
                {legendRangeMax && (
                    <div>
                        <HeatmapLegend
                            yMaxValue={legendRangeMax}
                            data={data}
                            width={LEGEND_WIDTH}
                            height={height}
                            yLegendLabel={yLegendLabel}
                            theme={theme}
                        />
                    </div>
                )}
            </StyledHeatMap>
        </>
    );
};

export type HeatmapProps = InternalHeatmapProps & {
    theme?: VizThemeKey;
};

export const Heatmap: React.FC<HeatmapProps> = ({ theme, ...props }) => {
    // If user has not passed an explicit `theme` value,
    // we don't need to wrap in extra context provider (it will use default or existing context)
    if (!theme) {
        return <InternalHeatmap {...props} />;
    }
    return (
        <SplunkThemeProvider colorScheme={theme}>
            <InternalHeatmap {...props} />
        </SplunkThemeProvider>
    );
};
